import React, { useCallback, useEffect, useRef, useState } from "react";
import { FEATURED_ROWS, FEATURED_ROWS_MOBILE } from "../data/site";
import { ArrowRight, ArrowUpRight } from "lucide-react";

/**
 * Tracks whether the viewport is mobile-sized (< md, 768px).
 * Used to swap the marquee item list on mobile — desktop stays 100% unchanged.
 */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 767px)").matches;
  });
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 767px)");
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);
  return isMobile;
}

/**
 * Marquee row with:
 *  - Infinite auto-scroll with 4 copies of items for guaranteed zero-gap loop
 *  - Pause on hover (desktop)
 *  - Drag / swipe support (touch + mouse)
 */
function MarqueeRow({ row, delayFrac = 0, running = true }) {
  // 4 copies × items → CSS translates -25% each loop (== 1 items-set width). Loop is
  // seamless (identical items periodically) and can never show a gap regardless of
  // viewport width.
  const COPIES = 4;
  const items = Array.from({ length: COPIES }).flatMap(() => row.items);

  const trackRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const state = useRef({ startX: 0, startTranslate: 0, currentTranslate: 0 });

  const getCurrentTranslate = () => {
    const el = trackRef.current;
    if (!el) return 0;
    const m = new DOMMatrixReadOnly(getComputedStyle(el).transform);
    return m.m41 || 0;
  };

  const onPointerDown = (e) => {
    const el = trackRef.current;
    if (!el) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    state.current.startX = x;
    state.current.startTranslate = getCurrentTranslate();
    state.current.currentTranslate = state.current.startTranslate;
    setDragging(true);
    el.style.animationPlayState = "paused";
    el.style.transform = `translate3d(${state.current.startTranslate}px, 0, 0)`;
  };

  const onPointerMove = useCallback((e) => {
    if (!dragging) return;
    const el = trackRef.current;
    if (!el) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    const delta = x - state.current.startX;
    state.current.currentTranslate = state.current.startTranslate + delta;
    el.style.transform = `translate3d(${state.current.currentTranslate}px, 0, 0)`;
  }, [dragging]);

  const onPointerUp = useCallback(() => {
    if (!dragging) return;
    setDragging(false);
    const el = trackRef.current;
    if (!el) return;
    // Each loop covers 1/COPIES of the total width, so wrap position modulo that.
    const loopWidth = el.scrollWidth / COPIES;
    let t = state.current.currentTranslate % loopWidth;
    if (t > 0) t -= loopWidth;
    const progress = -t / loopWidth;
    const duration = row.duration;
    const dir = row.reverse ? -1 : 1;
    const delay = -progress * duration * dir;
    el.style.transform = "";
    el.style.animationDelay = `${delay}s`;
    el.style.animationPlayState = "running";
  }, [dragging, row.duration, row.reverse]);

  useEffect(() => {
    const move = (e) => onPointerMove(e);
    const up = () => onPointerUp();
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: true });
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  }, [onPointerMove, onPointerUp]);

  return (
    <div className="marquee-wrap-4 overflow-hidden select-none">
      <div
        ref={trackRef}
        onMouseDown={onPointerDown}
        onTouchStart={onPointerDown}
        className={`marquee-track-4 flex gap-4 md:gap-5 py-2 ${
          dragging ? "cursor-grabbing" : "cursor-grab"
        } ${running ? "" : "marquee-idle"}`}
        style={{
          "--marquee-duration": `${row.duration}s`,
          animationDirection: row.reverse ? "reverse" : "normal",
          animationDelay: `${-delayFrac * row.duration}s`,
        }}
      >
        {items.map((src, i) => (
          <div
            key={i}
            className="shrink-0 w-[190px] md:w-[320px] thumb-card transition-transform duration-500 hover:-translate-y-1"
          >
            <img src={src} alt="Featured thumbnail" loading="lazy" draggable={false} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function FeaturedProjects() {
  const isMobile = useIsMobile();
  const rows = isMobile ? FEATURED_ROWS_MOBILE : FEATURED_ROWS;
  const sectionRef = useRef(null);
  const [running, setRunning] = useState(false);

  // Marquee stays parked until the section scrolls into view, so row 1 always
  // opens with the hero thumbnail (index 3) centered on first sight.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || running) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRunning(true);
          io.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [running]);

  // Row 1 start offset puts item index 3 (Netflix thumb) at the viewport center.
  const firstRowFrac = isMobile ? 0.314 : 0.08;
  return (
    <section ref={sectionRef} id="featured" className="relative py-6 md:py-14">
      <div className="max-w-6xl mx-auto px-5 md:px-8 text-center">
        {/* "Featured" small line with chevrons */}
        <div className="reveal relative inline-flex items-center justify-center gap-3">
          <span aria-hidden className="hidden md:flex items-center gap-1 mr-4 text-white/70">
            <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 6l6 6-6 6M12 6l6 6-6 6" />
            </svg>
          </span>
          <span
            className="text-white inline-flex items-center gap-2"
            style={{
              fontFamily: "'Clash Display',sans-serif",
              fontWeight: 500,
              fontSize: "clamp(22px, 3vw, 40px)",
              letterSpacing: "-0.01em",
            }}
          >
            Featured
            <ArrowUpRight className="w-5 h-5 md:w-7 md:h-7 text-white/85" strokeWidth={2} />
          </span>
          <span aria-hidden className="hidden md:flex items-center gap-1 ml-4 text-white/70">
            <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6l-6 6 6 6M12 6L6 12l6 6" />
            </svg>
          </span>
        </div>

        <h2
          className="reveal text-transparent bg-clip-text mt-1"
          style={{
            fontFamily: "'Clash Display',sans-serif",
            fontWeight: 500,
            fontSize: "clamp(60px, 9vw, 116px)",
            lineHeight: 0.95,
            letterSpacing: "-0.02em",
            backgroundImage:
              "linear-gradient(180deg,#EDE9FE 0%,#C4B5FD 50%,#8B5CF6 100%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            margin: 0,
          }}
        >
          Projects
        </h2>
        <p
          className="reveal mt-3 md:mt-4"
          style={{
            fontFamily: "'Karvest',sans-serif",
            fontWeight: 400,
            fontSize: "clamp(14px, 1.15vw, 20px)",
            lineHeight: 1.35,
            color: "rgba(255,255,255,0.72)",
            letterSpacing: "0.01em",
          }}
        >
          Real thumbnail concepts and designs
          <br />
          created for creators.
        </p>
      </div>

      {/* Marquee rows — bleed off viewport edges naturally (no hard cutout). Rows are
          staggered via negative animation-delay fractions (not padding) so no visible
          empty gap ever appears on the left side of any row. */}
      <div className="mt-10 md:mt-14 space-y-4 md:space-y-5">
        {rows.map((row, i) => {
          const delayFracs = [firstRowFrac, 0.18, 0.08, 0.24, 0.05];
          return (
            <MarqueeRow
              key={row.id}
              row={row}
              running={running}
              delayFrac={delayFracs[i % delayFracs.length]}
            />
          );
        })}
      </div>

      <div className="mt-10 md:mt-14 flex justify-center">
        <button
          className="group inline-flex items-center gap-3 pl-5 pr-2 py-2 rounded-full bg-white/95 text-[#1e1b4b] shadow-[0_10px_30px_rgba(79,70,229,0.35)] hover:bg-white hover:shadow-[0_14px_36px_rgba(139,92,246,0.45)] transition-all duration-300"
          style={{
            fontFamily: "'SF Pro Display',sans-serif",
            fontWeight: 400,
            fontSize: "14px",
            letterSpacing: "0.005em",
          }}
        >
          + many more
          <span className="w-8 h-8 rounded-full bg-[#0B0A1F] text-white flex items-center justify-center transition-transform duration-300 group-hover:translate-x-0.5">
            <ArrowRight className="w-4 h-4" />
          </span>
        </button>
      </div>

      {/* Scoped marquee animation — 4 copies × translate -25% == 1 items-set width per
          loop. This guarantees a seamless loop with zero visible gaps at any viewport. */}
      <style>{`
        @keyframes marquee-x-25 {
          0%   { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(-25%, 0, 0); }
        }
        .marquee-track-4 {
          width: max-content;
          animation: marquee-x-25 var(--marquee-duration, 24s) linear infinite;
          will-change: transform;
          touch-action: pan-y;
        }
        .marquee-track-4.marquee-idle { animation-play-state: paused; }
        @media (hover: hover) {
          .marquee-wrap-4:hover .marquee-track-4 {
            animation-play-state: paused;
          }
        }
      `}</style>
    </section>
  );
}
